<template>
  <div class="waw-intro">
    <ul class="waw-benefits">
      <li>Reach patients who prefer WhatsApp &mdash; including international and travelling patients.</li>
      <li>Replies land in your existing Conversations inbox, right alongside your text messages.</li>
      <li>Uses the credits you already have &mdash; a flat 1 credit per WhatsApp message.</li>
    </ul>

    <p class="waw-note">
      Your text messaging keeps working exactly as it does today. Nothing here changes it, and if
      you need a new number, we set one up for you at no extra charge.
    </p>

    <!-- Up-front, informational, NOT a gate: everyone sees this before answering anything, so a
         practice that already uses WhatsApp hears the trade-offs of moving its number before it is
         deep into the wizard. Worded conditionally because it is also shown to practices getting a
         new number, whom none of it applies to. It summarizes the SHAPE only — the full, exact
         wording (and the tick-to-confirm) come later on the acknowledgement screen, served from the
         backend; the six sentences are deliberately NOT duplicated here. -->
    <div class="waw-byo-heads-up">
      <h5 class="waw-byo-heads-up__title">Already use WhatsApp for your practice?</h5>
      <p class="waw-byo-heads-up__lead">
        If the number you want patients to message is one you already use in the WhatsApp app, moving
        it over to Teamio has a few knock-on effects worth knowing before you start:
      </p>
      <ul class="waw-byo-heads-up__list">
        <li>
          WhatsApp stops working on that number in the app you use today &mdash; every conversation
          moves into Teamio instead.
        </li>
        <li>Your past WhatsApp conversations on that number don&rsquo;t come across.</li>
        <li>
          Once it&rsquo;s moved, there are limits on when your practice can message a patient first,
          until they message you.
        </li>
      </ul>
      <p class="waw-byo-heads-up__foot">
        We&rsquo;ll go through each of these in full, and ask you to confirm them, before anything is
        set up. If you&rsquo;re getting a new number instead, none of this applies to you.
      </p>
    </div>
  </div>
</template>

<script>
/**
 * Wizard screen 1 — the pitch, and the honest expectation-setting.
 *
 * Everything that answers "what is this / what happens / what do I need" lives in wizardCopy.js and
 * is rendered by the frame. What is left here is the short benefit list (lifted verbatim from the
 * pitch card this wizard replaces, so a practice that saw the old tab sees the same three promises)
 * and one reassurance the copy trio has no slot for: this does not disturb texting, and it does not
 * add a bill. Those are the two things practices ask before they will start.
 *
 * The second half used to read "nothing is purchased without showing you the price first". Saumil's
 * 2026-09-02 decision made that both false and off-policy — Teamio pays for the number, and Twilio
 * number prices are never customer-facing — so it now states the actual answer: the number is
 * included. Do not reintroduce a price here.
 *
 * The bring-your-own heads-up panel (2026-09-03, Saumil: "even before they start the wizard, they
 * should be alerted about all those things") is informational, not a gate: it warns a practice that
 * already uses WhatsApp about the trade-offs of moving its number up front, worded conditionally
 * because everyone sees it. It summarizes the SHAPE only — the exact wording and the tick-to-confirm
 * live on the acknowledgement screen (WizardByoAckStep), served from the backend and never
 * duplicated here.
 *
 * No interactive controls of its own — the frame owns the single "Get started" button.
 */
export default {
  name: 'WizardIntroStep',
};
</script>

<style lang="scss" scoped>
@import '../../PatientPayments/tokens';

.waw-benefits {
  margin: 0 0 1rem;
  padding-left: 1.1rem;
  // Strong, not muted: this list is the pitch. $pp-text-muted on white is ~4.46:1 at ~12px, just under
  // the 4.5:1 AA minimum, and read as very pale (2026-10-10; pinned by whatsapp-wizard-intro-contrast.test.js).
  color: $pp-text-strong;
  // Regular weight, set here: an <li> inherits the app's light 300 (the <p> note below renders at 400),
  // and thin strokes at ~12px read as pale whatever the colour.
  font-weight: 400;
  font-size: 0.88rem;
  line-height: 1.6;
  max-width: 640px;

  li + li {
    margin-top: 0.3rem;
  }
}

.waw-note {
  margin: 0;
  padding: 0.65rem 0.85rem;
  border-radius: $pp-radius-sm;
  background: $pp-brand-subtle;
  border: 1px solid $pp-brand-border;
  color: $pp-text-strong;
  font-size: 0.83rem;
  line-height: 1.6;
  max-width: 640px;
}

.waw-byo-heads-up {
  margin: 1.2rem 0 0;
  padding: 0.9rem 1rem;
  border-radius: $pp-radius-sm;
  background: $pp-surface-sunken;
  border: 1px solid $pp-border;
  max-width: 640px;
}

.waw-byo-heads-up__title {
  margin: 0 0 0.5rem;
  font-size: 0.92rem;
  font-weight: 700;
  color: $pp-text-strong;
}

.waw-byo-heads-up__lead {
  margin: 0 0 0.6rem;
  font-size: 0.83rem;
  line-height: 1.6;
  color: $pp-text-muted;
}

.waw-byo-heads-up__list {
  margin: 0 0 0.6rem;
  padding-left: 1.1rem;
  // Regular weight, set here: an <li> inherits the app's light 300, so these warnings read thinner than the
  // lead and footer <p>s around them (400). Same cause as .waw-benefits (2026-10-10).
  font-weight: 400;
  font-size: 0.83rem;
  line-height: 1.6;
  color: $pp-text-muted;

  li + li {
    margin-top: 0.3rem;
  }
}

.waw-byo-heads-up__foot {
  margin: 0;
  font-size: 0.83rem;
  line-height: 1.6;
  color: $pp-text-muted;
}
</style>
